import { useCallback, useRef, useState } from 'react'
import { IconAiSpark, IconPaperPlane } from '../components/icons'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Spinner } from '../components/ui/Spinner'
import { apiClient } from '../lib/api/client'

export interface MaterialsAiModalProps {
  readonly open: boolean
  readonly onClose: () => void
}

interface ChatMessage {
  readonly role: 'user' | 'ai'
  readonly text: string
}

/** Design's "Types of questions to ask" starter prompts (`Fictiv Platform.dc.html:10573`). */
const SUGGESTED_QUESTIONS = [
  'Which CNC materials are best for high-strength applications?',
  "What's the best injection molded plastic for medical-grade production?",
  "What's a good 3D printing material with heat-resistant properties?",
]

/** `crypto.randomUUID()` with an RFC 4122 v4 fallback for environments that lack it
 * (mirrors `checkout-domain.ts`'s feature-detection precedent). */
function generateUuidV4(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * "Materials.AI" chat modal (UID-248) — the "Help and resources" section's
 * "Material selection" entry point, matching the design mock's modal
 * (`Fictiv Platform.dc.html:4002-4051`). POSTs the running conversation to
 * the BFF proxy (`POST /materials-ai/chat`), which forwards it to the
 * deployed Materials.AI service and returns its latest reply.
 *
 * From the Home entry point `link`/`quoteId`/`selectedProcess` are always
 * empty — those only apply from the (later) configurator entry point. Since
 * there is no `quoteId` here, every request instead carries a client-generated
 * `uuid` — stable for this component instance's lifetime (i.e. this chat
 * session), so closing/reopening the modal or reloading the page is fine to
 * mint a new one.
 */
export function MaterialsAiModal({ open, onClose }: MaterialsAiModalProps) {
  const [messages, setMessages] = useState<readonly ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [sessionUuid] = useState(generateUuidV4)
  const inputRef = useRef<HTMLInputElement>(null)

  async function send(text: string) {
    const question = text.trim()
    if (!question || loading) return

    setInput('')
    setError(undefined)
    const nextMessages = [...messages, { role: 'user' as const, text: question }]
    setMessages(nextMessages)
    setLoading(true)

    try {
      const { data, error: apiError } = await apiClient.POST('/materials-ai/chat', {
        body: {
          messages: nextMessages.map((m) => ({
            role: (m.role === 'user' ? 'user' : 'system') as 'user' | 'system',
            content: m.text,
          })),
          // No quoteId from this entry point — send the stable per-session uuid
          // in its place (the upstream service keys the conversation on one or
          // the other).
          uuid: sessionUuid,
        },
      })
      if (data) {
        setMessages((current) => [...current, { role: 'ai', text: data.reply }])
      } else {
        setError(
          (apiError as { detail?: string } | undefined)?.detail ??
            "Sorry — we couldn't reach the materials assistant just now. Please try again in a moment.",
        )
      }
    } catch {
      setError(
        "Sorry — we couldn't reach the materials assistant just now. Please try again in a moment.",
      )
    } finally {
      setLoading(false)
      inputRef.current?.focus()
    }
  }

  // Memoized so its reference stays stable across THIS component's own
  // re-renders (once per keystroke while composing a message) — Modal's
  // focus-management effect depends on `onClose`, so a fresh function here on
  // every render would re-run that effect on every keystroke and steal focus
  // from the input back to the dialog container mid-typing.
  const handleClose = useCallback(() => {
    onClose()
    setMessages([])
    setInput('')
    setError(undefined)
  }, [onClose])

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Materials.AI"
      maxWidth={600}
      headerContent={
        <div className="materials-ai-header">
          <div className="materials-ai-header-title">
            <IconAiSpark />
            <span>Materials.AI</span>
          </div>
          <p className="materials-ai-header-subtitle">Your AI assistant for material selection</p>
        </div>
      }
      footer={
        <Button variant="outline" onClick={handleClose}>
          Close
        </Button>
      }
    >
      <div className="materials-ai-body">
        {messages.length === 0 ? (
          <div className="materials-ai-suggestions">
            <p className="materials-ai-suggestions-label">Types of questions to ask:</p>
            {SUGGESTED_QUESTIONS.map((question) => (
              <button
                key={question}
                type="button"
                className="materials-ai-suggestion"
                onClick={() => void send(question)}
              >
                {question}
              </button>
            ))}
          </div>
        ) : (
          <div className="materials-ai-messages">
            {messages.map((message, index) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: append-only transcript, never reordered
                key={index}
                className={`materials-ai-message-row materials-ai-message-row-${message.role}`}
              >
                <p className={`materials-ai-bubble materials-ai-bubble-${message.role}`}>
                  {message.text}
                </p>
              </div>
            ))}
            {loading && (
              <div className="materials-ai-thinking">
                <Spinner size="sm" aria-hidden="true" />
                <span>Materials.AI is thinking…</span>
              </div>
            )}
          </div>
        )}
        {error && (
          <p role="alert" className="materials-ai-error">
            {error}
          </p>
        )}
      </div>
      <div className="materials-ai-composer">
        <Input
          ref={inputRef}
          label="Ask Materials.AI a question"
          hideLabel
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              void send(input)
            }
          }}
          placeholder="Type your question here"
          disabled={loading}
        />
        <Button
          aria-label="Send"
          onClick={() => void send(input)}
          disabled={!input.trim() || loading}
        >
          <IconPaperPlane />
        </Button>
      </div>
      <p className="materials-ai-disclaimer">
        Materials.AI is powered by Claude &amp; Fictiv's manufacturing database. For information
        purposes only.
      </p>
    </Modal>
  )
}
